import { awaitNavigationCommit } from "./navigationCommit";

/**
 * `document.startViewTransition` runs an async capture/animate/cleanup cycle that
 * outlives the `update` callback it's given. The callback resolving doesn't mean the
 * browser's transition has finished. Firing a second `startViewTransition` on the same
 * document while an earlier one is still live (e.g. pressing Done on two stacked
 * edit sheets back-to-back) overlaps their DOM mutation with React's own commit and
 * corrupts the fiber tree tree-wide ("Should have a queue" / `prevDeps.length`
 * errors in components unrelated to the page that navigated). Serializing
 * transitions through this queue keeps native chrome (title/back/Done button)
 * setup, which depends on the transition, while preventing the overlap.
 */
let currentTransition: Promise<void> = Promise.resolve();

export function queueViewTransition(update: () => Promise<void>, types: string[]): Promise<void> {
  const doc = document as any;
  if (!doc.startViewTransition) {
    return update();
  }

  const run = currentTransition
    .catch(() => {})
    .then(() => {
      // The update callback has to stay pending until React has the new screen on
      // screen, or the browser snapshots the old one twice and nothing slides.
      const transition = doc.startViewTransition({
        update: async () => {
          const committed = awaitNavigationCommit();
          await update();
          await committed;
        },
        types,
      });
      return transition.finished.catch(() => {});
    });

  currentTransition = run;
  return run;
}
